import React from "react";
import { createRoot } from "react-dom/client";
import { initObservability } from "./lib/observability";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthGate, AuthProvider } from "./lib/auth";
import ClientDetailPage from "./pages/ClientDetail";
import BusinessApplicationPage from "./pages/BusinessApplication";
import ClientsPage from "./pages/Clients";
import DashboardPage from "./pages/Dashboard";
import IntakePage from "./pages/Intake";
import StartIntakePage from "./pages/StartIntake";
import ContactPage from "./pages/Contact";
import { PrivacyPage, TermsPage, DisclosurePage } from "./pages/Legal";
import LoginPage from "./pages/Login";
import NewClientPage from "./pages/NewClient";
import PortalAgreementSignPage from "./pages/PortalAgreementSign";
import PortalDashboardPage from "./pages/PortalDashboard";
import PortalHistoryPage from "./pages/PortalHistory";
import PortalOffersPage from "./pages/PortalOffers";
import PortalCreditAccessPage from "./pages/PortalCreditAccess";
import PortalCreditAuthorizationPage from "./pages/PortalCreditAuthorization";
import DetailsFormPage from "./pages/DetailsForm";
import UploadDocumentsPage from "./pages/UploadDocuments";
import JoinFunnelPage from "./pages/JoinFunnel";
import PortalLoginPage from "./pages/PortalLogin";
import PortalSetPasswordPage from "./pages/PortalSetPassword";
import PortalSentPage from "./pages/PortalSent";
import PortalUploadPage from "./pages/PortalUpload";
import PortalVerifyPage from "./pages/PortalVerify";
import BillingPage from "./pages/Billing";
import LendersPage from "./pages/Lenders";
import ReportsPage from "./pages/Reports";
import { StubPage } from "./pages/Stub";
import ContractsPage from "./pages/Contracts";
import AffiliatesPage from "./pages/Affiliates";
import { AffiliateLoginPage, AffiliateDashboardPage } from "./pages/AffiliatePortal";
import IntegrationsPage from "./pages/Integrations";
import SettingsPage from "./pages/Settings";
import TrainingPage from "./pages/Training";
import AdminPage from "./pages/Admin";
import JoinPage from "./pages/Join";
import SuspendedPage from "./pages/Suspended";
import StaffInvitePage from "./pages/StaffInvite";
import VoeSignPage from "./pages/VoeSign";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPassword";
import "./styles.css";

/**
 * Subdomain isolation:
 *
 * The same Vite bundle is served on app.example.com and
 * portal.example.com (Cloudflare Worker custom_domain routes both
 * hostnames at the same Worker, which falls back to index.html for any path
 * via not_found_handling = single-page-application).
 *
 * Without runtime host detection, a visitor to portal.* would see the staff
 * route tree (including AuthGate-protected /clients, /letters, /dashboard,
 * etc.). Even though those routes redirect anonymous users to /login, the
 * staff sign-in form itself leaks the existence of the staff app and — much
 * worse — anyone who has a stale `nebm_session` cookie on portal.* (e.g. a
 * shared-device scenario, or a future cookie-handling bug) would see staff
 * data on a URL the client is expected to use.
 *
 * Fix: detect hostname at startup, render a portal-only <Routes> tree on
 * portal.*, and the existing staff tree everywhere else (app.*, apex,
 * workers.dev fallback, localhost for dev).
 *
 * Defensive: portal.* MUST NOT mount /dashboard, /clients, /letters, etc.
 * even if a staff cookie is somehow present. The portal tree below only
 * knows about /portal/*.
 */
const isPortalHost =
  typeof window !== "undefined" &&
  /^portal\./i.test(window.location.hostname);

function PortalApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/start/:slug" element={<StartIntakePage />} />
        {/* Client portal routes only. No AuthGate (staff), no AuthProvider:
            the portal uses its own cookie (`nebm_portal`) and the
            PortalDashboard / PortalUpload / PortalHistory pages each call
            the portal API directly and redirect to /portal/login on 401. */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />
        <Route path="/portal/sent" element={<PortalSentPage />} />
        <Route path="/portal/verify" element={<PortalVerifyPage />} />
        <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
        <Route path="/portal/upload" element={<PortalUploadPage />} />
        <Route path="/portal/history" element={<PortalHistoryPage />} />
        <Route path="/portal/offers" element={<PortalOffersPage />} />
        <Route path="/portal/credit-access" element={<PortalCreditAccessPage />} />
        <Route path="/portal/credit-authorization" element={<PortalCreditAuthorizationPage />} />
        <Route path="/portal/agreements/sign" element={<PortalAgreementSignPage />} />

        {/* Root and everything else on portal.* → portal login.
            We deliberately do NOT mount /login, /dashboard, /clients,
            /letters, /reports, /disputes, /mail, /compliance, /billing,
            /referrals, or /integrations here. A visitor who lands on any
            of those paths gets bounced to the client sign-in. */}
        <Route path="/" element={<Navigate to="/portal/login" replace />} />
        <Route path="*" element={<Navigate to="/portal/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function StaffApp() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* PUBLIC — "/" is the marketing landing page (also the URL given
              on affiliate/partner applications); staff sign-in lives at
              /login. Signed-in staff just click Sign in → AuthGate passes
              them straight through to the dashboard. */}
          {/* White-label install: this is Tanya's private platform, not a software
              storefront. Root goes to staff sign-in; the $200/mo self-serve
              pricing/checkout (the standalone SaaS offer) is intentionally not mounted. */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/disclosure" element={<DisclosurePage />} />
          <Route path="/details/:token" element={<DetailsFormPage />} />
          <Route path="/upload-docs/:token" element={<UploadDocumentsPage />} />
          <Route path="/with/:slug" element={<JoinFunnelPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/staff-invite/:token" element={<StaffInvitePage />} />
          <Route path="/voe/:token" element={<VoeSignPage />} />
          <Route path="/intake/:token" element={<IntakePage />} />
          <Route path="/apply/:token" element={<BusinessApplicationPage />} />
          <Route path="/start/:slug" element={<StartIntakePage />} />

          {/* CLIENT PORTAL — cookie-authed, no staff AuthGate.
              Kept on the staff hostname as a defensive fallback (e.g. an old
              magic-link email pointing at app.* still resolves), though the
              canonical portal URL is portal.*. */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />
          <Route path="/portal/sent" element={<PortalSentPage />} />
          <Route path="/portal/verify" element={<PortalVerifyPage />} />
          <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
          <Route path="/portal/upload" element={<PortalUploadPage />} />
          <Route path="/portal/history" element={<PortalHistoryPage />} />
        <Route path="/portal/offers" element={<PortalOffersPage />} />
        <Route path="/portal/credit-access" element={<PortalCreditAccessPage />} />
        <Route path="/portal/credit-authorization" element={<PortalCreditAuthorizationPage />} />
          <Route path="/portal/agreements/sign" element={<PortalAgreementSignPage />} />

          {/* AUTHED */}
          <Route path="/dashboard" element={<AuthGate><DashboardPage /></AuthGate>} />
          <Route path="/clients" element={<AuthGate><ClientsPage /></AuthGate>} />
          <Route path="/clients/new" element={<AuthGate><NewClientPage /></AuthGate>} />
          <Route path="/clients/:id" element={<AuthGate><ClientDetailPage /></AuthGate>} />
          <Route path="/reports" element={<AuthGate><ReportsPage /></AuthGate>} />
          <Route path="/lenders" element={<AuthGate><LendersPage /></AuthGate>} />
          <Route path="/contracts" element={<AuthGate><ContractsPage /></AuthGate>} />
          <Route path="/affiliates" element={<AuthGate><AffiliatesPage /></AuthGate>} />
          <Route path="/billing" element={<AuthGate><BillingPage /></AuthGate>} />
          <Route path="/integrations" element={<AuthGate><IntegrationsPage /></AuthGate>} />
          <Route path="/settings" element={<AuthGate><SettingsPage /></AuthGate>} />
          {/* Training — written lessons + videos. Read-only content, so every
              staff role (including view-only) may open it. */}
          <Route path="/training" element={<AuthGate><TrainingPage /></AuthGate>} />
          <Route path="/training/:slug" element={<AuthGate><TrainingPage /></AuthGate>} />
          <Route path="/admin" element={<AuthGate><AdminPage /></AuthGate>} />
          {/* Public — a new company accepts an invite (no staff auth). */}
          <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
          <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
          <Route path="/join/:token" element={<JoinPage />} />
          {/* Shown when a company's access is paused (402 company_suspended). */}
          <Route path="/suspended" element={<SuspendedPage />} />

          {/* fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function App() {
  return isPortalHost ? <PortalApp /> : <StaffApp />;
}

initObservability();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// PWA: register the service worker so Android offers "Install app" and
// iOS Add-to-Home-Screen runs standalone.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}
